import { Component, OnInit, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
// import { TabViewModule } from 'primeng/tabview';
// import { AutoCompleteModule } from 'primeng/autocomplete';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
// import { DividerModule } from 'primeng/divider';
// import { TagModule } from 'primeng/tag';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';

import { ProductService } from '../../../core/services/product.service';
import { CustomerService } from '../../../core/services/customer.service';
import { CustomerLookupService, LookupParams, LookupResult, BusinessSuggestion, ManualAddressCandidate, TerritoryBoundary } from '../../../core/services/customer-lookup.service';
import { SETTINGS } from '../../../core/config/common.settings';
import { EncryptionService } from '../../../core/services/encryption';
import { AddEditCustomer } from '../add-edit-customer/add-edit-customer';

interface ProductOption {
  label: string;
  value: string;
}

interface CountryOption {
  label: string;
  value: string;
}

// Interfaces now imported from service

@Component({
  selector: 'app-customer-lookup',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    ToastModule,
    TooltipModule,
    DrawerModule,
    DialogModule,
    ProgressSpinnerModule,
    PaginatorModule,
    AddEditCustomer
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './customer-lookup.html'
})
export class CustomerLookup implements OnInit, OnDestroy {
  @ViewChild('businessInput') businessInput!: ElementRef<HTMLInputElement>;
  @ViewChild('manualAddressMap') manualAddressMapElement?: ElementRef<HTMLDivElement>;
  @ViewChild('manualGeofenceMap') manualGeofenceMapElement?: ElementRef<HTMLDivElement>;

  // User context
  loggedInUserRole: string = '';
  loggedInUserID: string = '';
  loggedInUserSubscriberId: string = '';

  // Form data
  selectedProducts: string[] = []; // Array of product IDs
  productOptions: ProductOption[] = [];
  loadingProducts = true;
  
  // Search methods
  lookupModes = [
    { label: 'Google Place Search', value: 'GOOGLE' },
    { label: 'Manual Address', value: 'MANUAL_ADDRESS' },
    { label: 'Manual Geofence', value: 'MANUAL_GEOFENCE' }
  ];
  lookupMode: 'GOOGLE' | 'MANUAL_ADDRESS' | 'MANUAL_GEOFENCE' = 'GOOGLE';
  searchQuery: string = '';
  businessSuggestions: BusinessSuggestion[] = [];

  // Address suggestions
  citySuggestions: string[] = [];
  streetSuggestions: string[] = [];
  showCitySuggestions: boolean = false;
  showStreetSuggestions: boolean = false;
  
  // Prevent immediate closure flags
  private preventClosure: boolean = false;

  // Address search results
  addressBusinessResults: any[] = [];
  showAddressBusinessResults: boolean = false;

  // Map functionality
  mapSearchQuery: string = '';
  mapBusinessSuggestions: any[] = [];
  map: any = null;
  markers: any[] = [];
  
  // Address search
  selectedCountry: CountryOption | null = null;
  countryOptions: CountryOption[] = [];
  city: string = '';
  streetAddress: string = '';
  addressLine2: string = '';
  stateRegion: string = '';
  postalCode: string = '';

  // Manual address lookup
  manualAddressReady = false;
  manualAddressMap?: google.maps.Map;
  manualAddressMarker?: google.maps.Marker;
  manualGeocodedLocation: { lat: number; lng: number } | null = null;
  manualPlaceName: string = '';
  manualAddressPlaceId: string | null = null;
  manualAddressMatches: ManualAddressCandidate[] = [];
  manualAddressMatchesTotal = 0;
  manualAddressMatchesPage = 1;
  manualAddressMatchesPageSize = 5;
  manualAddressMatchesLoading = false;
  manualAddressSelectedCandidate: ManualAddressCandidate | null = null;
  manualAddressConflicts: ManualAddressCandidate[] = [];

  // Manual geofence lookup
  manualGeofenceMap?: google.maps.Map;
  manualGeofenceCenterMarker?: google.maps.Marker;
  manualGeofenceCircle?: google.maps.Circle;
  manualGeofenceCenter: { lat: number; lng: number } | null = null;
  manualGeofenceRadius: number = 3;
  manualGeofenceReady = false;
  manualGeofenceLabel: string = '';
  manualGeofenceMapClickListener?: google.maps.MapsEventListener;
  manualGeofenceConflicts: ManualAddressCandidate[] = [];
  manualGeofenceSearchQuery: string = '';
  manualGeofenceSearchLoading = false;
  private isDraggingRadius = false;
  private radiusChangeTimeout?: ReturnType<typeof setTimeout>;

  // Territory overlays
  territoryBoundaries: TerritoryBoundary[] = [];
  private territoryPolygons: google.maps.Polygon[] = [];
  private territoryFetchAttempted = false;

  // Google cache
  private googlePlaceDetailsCache = new Map<string, {
    coordinates: { lat: number; lng: number };
    name?: string;
    formattedAddress?: string;
  }>();
  private selectedGooglePlaceDetails: {
    placeId: string;
    coordinates?: { lat: number; lng: number };
    name?: string;
    formattedAddress?: string;
  } | null = null;

  private readonly COORD_KEY_PRECISION = 5;
  private readonly MIN_GEOFENCE_RADIUS = 3;
  private readonly DEFAULT_GOOGLE_RADIUS = 3;
  private readonly MANUAL_CANDIDATE_RADIUS = 50;
  private readonly DUPLICATE_ALERT_RADIUS = 10;
  private readonly DUPLICATE_DISTANCE_THRESHOLD_METERS = 10;

  // Results
  lookupResults: LookupResult[] = [];
  loading = false;

  // Drawer state
  showAddCustomerDrawer = false;
  selectedLookupResult: LookupResult | null = null;
  customerCreatedSuccess = false;
  
  // Dialog state for post-creation popup
  showPostCreationDialog = false;
  createdCustomerData: any = null;

  // Google Maps integration
  private autocompleteService?: google.maps.places.AutocompleteService;
  private placesService?: google.maps.places.PlacesService;
  private isGoogleMapsLoaded = false;
  
  // Event listener cleanup
  private clickListener?: (event: Event) => void;

  constructor(
    private productService: ProductService,
    private customerService: CustomerService,
    private customerLookupService: CustomerLookupService,
    private messageService: MessageService,
    private encryption: EncryptionService,
    private ngZone: NgZone,
    private router: Router,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadProducts();
    this.loadCountries();
    this.initializeGoogleMaps();
    // this.initializeMap();
    
    // Close suggestions when clicking outside
    this.clickListener = (event: Event) => {
      // Skip if we're preventing closure
      if (this.preventClosure) {
        return;
      }
      
      const target = event.target as HTMLElement;
      
      // Check if click is within any suggestion dropdown, form field, or input
      const isWithinSuggestions = target.closest('.suggestions-dropdown') || 
                                 target.closest('.form-field') ||
                                 target.closest('[data-suggestions]') ||
                                 target.closest('input') ||
                                 target.tagName === 'INPUT';
      
      if (!isWithinSuggestions) {
        this.businessSuggestions = [];
        this.showCitySuggestions = false;
        this.showStreetSuggestions = false;
      }
    };
    document.addEventListener('click', this.clickListener);
  }

  performManualGeofenceLookup(): void {
    if (this.lookupMode !== 'MANUAL_GEOFENCE') {
      return;
    }

    // Clear past lookup results when starting new lookup
    this.lookupResults = [];
    // Reset product selection to all products
    this.resetProductSelection();

    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Product Selected',
        detail: 'Please select at least one product'
      });
      return;
    }

    if (!this.manualGeofenceCenter) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Geofence Not Set',
        detail: 'Click on the map to choose a center for the geofence'
      });
      return;
    }

    const radius = Math.max(this.manualGeofenceRadius, this.MIN_GEOFENCE_RADIUS);
    const lookupParams: LookupParams = {
      productIds: this.selectedProducts,
      coordinates: this.manualGeofenceCenter,
      lookupSource: 'MANUAL_GEOFENCE',
      manualGeofence: {
        center: this.manualGeofenceCenter,
        radiusMeters: radius,
        label: this.manualGeofenceLabel ? this.manualGeofenceLabel.trim() : undefined
      }
    };

    this.loading = true;
    this.customerLookupService.performLookup(lookupParams).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.handleLookupResult(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Failed',
            detail: response.message || 'Failed to perform geofence lookup'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error performing geofence lookup:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Lookup Error',
          detail: error.error?.message || 'Failed to perform geofence lookup'
        });
      }
    });
  }

  private loadCurrentUser(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      this.loggedInUserRole = typeof user.role === 'object' ? user.role?.identification : user.role;
      this.loggedInUserID = user._id;
      this.loggedInUserSubscriberId = typeof user.subscriberId === 'object' ? user.subscriberId?._id : user.subscriberId;
    }
  }

  private loadProducts(): void {
    this.loadingProducts = true;
    // Use the new API to get user-assigned active products
    this.customerLookupService.getUserAssignedProducts().subscribe({
      next: (response) => {
        if (response.success) {
          this.productOptions = response.data.map((product: any) => ({
            label: product.name,
            value: product._id
          }));
          // Auto-select all products by default
          this.selectedProducts = this.productOptions.map(p => p.value);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.message || 'Failed to load products'
          });
        }
        this.loadingProducts = false;
      },
      error: (error) => {
        console.error('Error loading products:', error);
        this.loadingProducts = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load assigned products'
        });
      }
    });
  }

  // Reset product selection to all products
  private resetProductSelection(): void {
    if (this.productOptions && this.productOptions.length > 0) {
      this.selectedProducts = this.productOptions.map(p => p.value);
    }
  }

  private loadCountries(): void {
    this.countryOptions = [
      { label: 'United States', value: 'US' },
      { label: 'United Kingdom', value: 'UK' },
      { label: 'Germany', value: 'DE' },
      { label: 'France', value: 'FR' },
      { label: 'Australia', value: 'AU' },
      { label: 'Canada', value: 'CA' },
      { label: 'Sri Lanka', value: 'LK' },
      { label: 'Netherlands', value: 'NL' },
      { label: 'Switzerland', value: 'CH' },
      { label: 'Sweden', value: 'SE' }
    ];
  }

  private async initializeGoogleMaps(): Promise<void> {
    // Wait for Google Maps to be loaded
    if (typeof google === 'undefined' || !google.maps) {
      setTimeout(() => this.initializeGoogleMaps(), 500);
      return;
    }

    this.isGoogleMapsLoaded = true;
    this.autocompleteService = new google.maps.places.AutocompleteService();
    
    // Create a dummy map for PlacesService (not displayed)
    const dummyMap = new google.maps.Map(document.createElement('div'));
    this.placesService = new google.maps.places.PlacesService(dummyMap);

    if (this.lookupMode === 'MANUAL_GEOFENCE') {
      setTimeout(() => this.prepareGeofenceMap(), 0);
    }

    this.loadFsrTerritories();
  }

  // Business search autocomplete using API
  searchBusiness(event: any): void {
    const query = event.query;
    if (query.length < 3) {
      this.businessSuggestions = [];
      return;
    }

    this.customerLookupService.searchBusinesses(query).subscribe({
      next: (response) => {
        if (response.success) {
          this.businessSuggestions = response.data;
        } else {
          this.businessSuggestions = [];
        }
      },
      error: (error) => {
        console.error('Error searching businesses:', error);
        this.businessSuggestions = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Search Error',
          detail: 'Failed to search businesses. Please try again.'
        });
      }
    });
  }

  onLookupModeChange(mode: 'GOOGLE' | 'MANUAL_ADDRESS' | 'MANUAL_GEOFENCE'): void {
    this.lookupMode = mode;

    if (mode !== 'GOOGLE') {
      this.businessSuggestions = [];
      this.searchQuery = '';
    }

    if (mode !== 'MANUAL_ADDRESS') {
      this.resetManualAddressState();
    }

    if (mode !== 'MANUAL_GEOFENCE') {
      this.resetManualGeofenceState();
    } else {
      setTimeout(() => {
        this.prepareGeofenceMap();
        this.loadFsrTerritories();
      }, 0);
    }
  }

  private resetManualAddressState(): void {
    this.manualAddressReady = false;
    this.manualGeocodedLocation = null;
    this.manualPlaceName = '';
    this.manualAddressPlaceId = null;
    this.manualAddressMatches = [];
    this.manualAddressMatchesTotal = 0;
    this.manualAddressMatchesPage = 1;
    this.manualAddressSelectedCandidate = null;
    this.manualAddressConflicts = [];
    this.manualAddressMatchesLoading = false;
    if (this.manualAddressMarker) {
      this.manualAddressMarker.setMap(null);
      this.manualAddressMarker = undefined;
    }
    if (this.manualAddressMap) {
      this.manualAddressMap = undefined;
    }
  }

  private resetManualGeofenceState(): void {
    this.manualGeofenceReady = false;
    this.manualGeofenceCenter = null;
    this.manualGeofenceRadius = this.MIN_GEOFENCE_RADIUS;
    this.manualGeofenceLabel = '';
    this.manualGeofenceConflicts = [];
    this.manualGeofenceSearchQuery = '';
    this.manualGeofenceSearchLoading = false;
    this.mapBusinessSuggestions = [];
    if (this.manualGeofenceCircle) {
      this.manualGeofenceCircle.setMap(null);
      this.manualGeofenceCircle = undefined;
    }
    if (this.manualGeofenceCenterMarker) {
      this.manualGeofenceCenterMarker.setMap(null);
      this.manualGeofenceCenterMarker = undefined;
    }
    if (this.manualGeofenceMapClickListener) {
      this.manualGeofenceMapClickListener.remove();
      this.manualGeofenceMapClickListener = undefined;
    }
  }

  private renderManualAddressMap(position: { lat: number; lng: number }): void {
    if (typeof google === 'undefined' || !google.maps) {
      setTimeout(() => this.renderManualAddressMap(position), 200);
      return;
    }

    if (!this.manualAddressMapElement) {
      setTimeout(() => this.renderManualAddressMap(position), 0);
      return;
    }

    if (!this.manualAddressMap) {
      this.manualAddressMap = new google.maps.Map(this.manualAddressMapElement.nativeElement, {
        center: position,
        zoom: 17,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        fullscreenControl: true, 
      });
    } else {
      this.manualAddressMap.setCenter(position);
    }

    if (this.manualAddressMarker) {
      this.manualAddressMarker.setMap(null);
    }

    this.manualAddressMarker = new google.maps.Marker({
      position,
      map: this.manualAddressMap,
      draggable: true
    });

    this.manualAddressMarker.addListener('dragend', (event: any) => {
      this.ngZone.run(() => {
        this.manualGeocodedLocation = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
      });
    });

  }

  private prepareGeofenceMap(): void {
    if (typeof google === 'undefined' || !google.maps) {
      setTimeout(() => this.prepareGeofenceMap(), 200);
      return;
    }

    if (!this.manualGeofenceMapElement) {
      if (this.lookupMode === 'MANUAL_GEOFENCE') {
        setTimeout(() => this.prepareGeofenceMap(), 0);
      }
      return;
    }

    if (!this.manualGeofenceMap) {
      const defaultCenter = this.manualGeocodedLocation || { lat: 40.7128, lng: -74.0060 };
      this.manualGeofenceMap = new google.maps.Map(this.manualGeofenceMapElement.nativeElement, {
        center: defaultCenter,
        zoom: 14,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        streetViewControl: false,
        fullscreenControl: true
      });
    }

    if (!this.manualGeofenceMapClickListener && this.manualGeofenceMap) {
      this.manualGeofenceMapClickListener = this.manualGeofenceMap.addListener('click', (event: any) => {
        this.ngZone.run(() => {
          this.setGeofenceCenter({
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          });
        });
      });
    }

    if (this.manualGeofenceCenter) {
      this.setGeofenceCenter(this.manualGeofenceCenter);
    }

    this.applyTerritoryOverlays();
  }

  private loadFsrTerritories(): void {
    if (this.territoryFetchAttempted) {
      this.applyTerritoryOverlays();
      return;
    }

    this.territoryFetchAttempted = true;

    this.customerLookupService.getFsrTerritoryBoundaries().subscribe({
      next: (response) => {
        this.territoryBoundaries = response?.territories ?? [];
        this.applyTerritoryOverlays();
      },
      error: (error) => {
        console.error('Error loading territory boundaries:', error);
      }
    });
  }

  private applyTerritoryOverlays(): void {
    if (!this.manualGeofenceMap) {
      return;
    }

    this.clearTerritoryOverlays();

    if (!this.territoryBoundaries || this.territoryBoundaries.length === 0) {
      return;
    }

    this.territoryBoundaries.forEach((boundary, index) => {
      if (!boundary.polygon || boundary.polygon.length === 0) {
        return;
      }

      const strokeColor = boundary.color || this.getTerritoryColor(index);
      const fillColor = this.applyAlphaToHex(strokeColor, 0.08);

      const polygon = new google.maps.Polygon({
        paths: boundary.polygon,
        map: this.manualGeofenceMap!,
        strokeColor,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor,
        fillOpacity: 0.35,
        clickable: false
      });

      this.territoryPolygons.push(polygon);
    });
  }

  private clearTerritoryOverlays(): void {
    this.territoryPolygons.forEach(polygon => polygon.setMap(null));
    this.territoryPolygons = [];
  }

  private getTerritoryColor(index: number): string {
    const palette = ['#2563eb', '#16a34a', '#db2777', '#f97316', '#8b5cf6', '#0ea5e9'];
    return palette[index % palette.length];
  }

  private applyAlphaToHex(hexColor: string, alpha: number): string {
    const normalized = hexColor.replace('#', '');
    if (normalized.length !== 6) {
      return hexColor;
    }
    const r = parseInt(normalized.substring(0, 2), 16);
    const g = parseInt(normalized.substring(2, 4), 16);
    const b = parseInt(normalized.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private setGeofenceCenter(position: { lat: number; lng: number }): void {
    if (!this.manualGeofenceMap) {
      return;
    }

    if (!this.isWithinAssignedTerritories(position)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Outside Territory',
        detail: 'You can only place reservations inside your assigned territories.'
      });
      return;
    }

    this.manualGeofenceCenter = position;

    if (!this.manualGeofenceCenterMarker) {
      this.manualGeofenceCenterMarker = new google.maps.Marker({
        position,
        map: this.manualGeofenceMap,
        draggable: true
      });

      this.manualGeofenceCenterMarker.addListener('dragend', (event: any) => {
        this.ngZone.run(() => {
          this.setGeofenceCenter({
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          });
        });
      });
    } else {
      this.manualGeofenceCenterMarker.setPosition(position);
    }

    if (!this.manualGeofenceCircle) {
      this.manualGeofenceCircle = new google.maps.Circle({
        map: this.manualGeofenceMap,
        center: position,
        radius: this.manualGeofenceRadius,
        fillColor: '#3182ce',
        fillOpacity: 0.15,
        strokeColor: '#2b6cb0',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        editable: false
      });
    } else {
      this.manualGeofenceCircle.setCenter(position);
      this.manualGeofenceCircle.setRadius(this.manualGeofenceRadius);
    }

    this.manualGeofenceMap.setCenter(position);
    this.manualGeofenceReady = true;
    this.evaluateGeofenceConflicts();
  }

  onGeofenceRadiusChange(radius: number): void {
    this.manualGeofenceRadius = radius;
    if (this.manualGeofenceCircle) {
      this.manualGeofenceCircle.setRadius(radius);
    }
    // Don't call API during dragging - only update visual
  }

  onGeofenceRadiusInputStart(): void {
    this.isDraggingRadius = true;
    // Clear any pending timeout
    if (this.radiusChangeTimeout) {
      clearTimeout(this.radiusChangeTimeout);
    }
  }

  onGeofenceRadiusInputEnd(): void {
    this.isDraggingRadius = false;
    // Clear any pending timeout
    if (this.radiusChangeTimeout) {
      clearTimeout(this.radiusChangeTimeout);
    }
    // Call API only when user releases
    this.evaluateGeofenceConflicts();
  }

  private isWithinAssignedTerritories(position: { lat: number; lng: number }): boolean {
    if (!this.territoryBoundaries || this.territoryBoundaries.length === 0) {
      return true;
    }

    return this.territoryBoundaries.some(boundary =>
      boundary.polygon && boundary.polygon.length > 2 && this.isPointInsidePolygon(position, boundary.polygon)
    );
  }

  private isPointInsidePolygon(point: { lat: number; lng: number }, polygon: Array<{ lat: number; lng: number }>): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lng;
      const yi = polygon[i].lat;
      const xj = polygon[j].lng;
      const yj = polygon[j].lat;

      const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
        (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi + Number.EPSILON) + xi);
      if (intersect) {
        inside = !inside;
      }
    }
    return inside;
  }

  private evaluateGeofenceConflicts(): void {
    if (!this.manualGeofenceCenter) {
      this.manualGeofenceConflicts = [];
      return;
    }

    const radius = Math.max(this.manualGeofenceRadius, this.MIN_GEOFENCE_RADIUS);

    this.customerLookupService.checkExistingCustomersInRadius({
      lat: this.manualGeofenceCenter.lat,
      lng: this.manualGeofenceCenter.lng,
      radiusMeters: radius
    }).subscribe({
      next: (response) => {
        this.manualGeofenceConflicts = response?.matches ?? [];
        if (this.manualGeofenceConflicts.length > 0) {
          const conflictNames = this.manualGeofenceConflicts
            .map(match => match.name || match.formattedAddress || match.placeId)
            .filter(name => !!name)
            .slice(0, 3)
            .join(', ');

          this.messageService.add({
            severity: 'warn',
            summary: 'Existing Customer Nearby',
            detail: conflictNames
              ? `There are existing records within ${radius}m: ${conflictNames}`
              : `There are existing records within ${radius}m.`
          });
        }
      },
      error: (error) => {
        console.error('Error checking geofence conflicts:', error);
      }
    });
  }

  private loadManualAddressCandidates(): void {
    if (!this.manualGeocodedLocation) {
      this.manualAddressMatches = [];
      this.manualAddressMatchesTotal = 0;
      this.manualAddressMatchesLoading = false;
      return;
    }

    this.manualAddressMatchesLoading = true;
    this.customerLookupService.searchManualAddressCandidates({
      lat: this.manualGeocodedLocation.lat,
      lng: this.manualGeocodedLocation.lng,
      radiusMeters: this.MANUAL_CANDIDATE_RADIUS,
      page: this.manualAddressMatchesPage,
      pageSize: this.manualAddressMatchesPageSize
    }).subscribe({
      next: (response) => {
        this.manualAddressMatches = response?.data ?? [];
        this.manualAddressMatchesTotal = response?.total ?? this.manualAddressMatches.length;

        if (!this.manualAddressSelectedCandidate && this.manualAddressMatches.length > 0 && this.manualGeocodedLocation) {
          const autoCandidate = this.manualAddressMatches.find(candidate =>
            candidate.placeId &&
            !this.isSyntheticPlaceId(candidate.placeId) &&
            this.coordinatesClose(candidate.coordinates, this.manualGeocodedLocation!)
          );
          if (autoCandidate) {
            this.selectManualAddressCandidate(autoCandidate);
          }
        }
      },
      error: (error) => {
        console.error('Error loading manual address candidates:', error);
        this.manualAddressMatches = [];
        this.manualAddressMatchesTotal = 0;
        this.manualAddressMatchesLoading = false;
      },
      complete: () => {
        this.manualAddressMatchesLoading = false;
      }
    });
  }

  onManualAddressPageChange(event: PaginatorState): void {
    const page = typeof event.page === 'number' ? event.page : 0;
    const rows = typeof event.rows === 'number' ? event.rows : this.manualAddressMatchesPageSize;

    this.manualAddressMatchesPage = page + 1;
    this.manualAddressMatchesPageSize = rows;
    this.loadManualAddressCandidates();
  }

  selectManualAddressCandidate(candidate: ManualAddressCandidate): void {
    this.manualAddressSelectedCandidate = candidate;
    this.manualAddressPlaceId = candidate.placeId ?? null;

    if (candidate.name) {
      this.manualPlaceName = candidate.name;
    }

    if (candidate.formattedAddress) {
      this.streetAddress = candidate.formattedAddress;
    }

    if (candidate.coordinates) {
      this.manualGeocodedLocation = candidate.coordinates;
      this.renderManualAddressMap(candidate.coordinates);
    }

    if (candidate.placeId) {
      this.fetchGooglePlaceDetails(candidate.placeId).then(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Google Place Linked',
          detail: 'This manual address will reuse the Google listing that already exists.'
        });
      }).catch(() => undefined);
    }

    this.evaluateManualAddressConflicts();
  }

  private evaluateManualAddressConflicts(): void {
    if (!this.manualGeocodedLocation) {
      this.manualAddressConflicts = [];
      return;
    }

    this.customerLookupService.checkExistingCustomersInRadius({
      lat: this.manualGeocodedLocation.lat,
      lng: this.manualGeocodedLocation.lng,
      radiusMeters: this.DUPLICATE_ALERT_RADIUS
    }).subscribe({
      next: (response) => {
        this.manualAddressConflicts = response?.matches ?? [];
        if (this.manualAddressConflicts.length > 0) {
          const conflict = this.manualAddressConflicts[0];
          const name = conflict.name || conflict.formattedAddress || conflict.placeId;
          this.messageService.add({
            severity: 'warn',
            summary: 'Possible Duplicate',
            detail: name
              ? `A customer "${name}" already exists near this location.`
              : 'A customer already exists near this location.'
          });
        }
      },
      error: (error) => {
        console.error('Error checking manual address conflicts:', error);
      }
    });
  }

  // Business search using API
  performBusinessSearch(): void {
    // Clear past lookup results when starting new lookup
    this.lookupResults = [];
    // Reset product selection to all products
    this.resetProductSelection();
    
    if (!this.searchQuery || !this.selectedProducts || this.selectedProducts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid Search',
        detail: 'Please enter a business name and select at least one product'
      });
      return;
    }

    this.loading = true;

    const lookupParams: LookupParams = {
      productIds: this.selectedProducts,
      searchQuery: this.searchQuery
    };

    this.customerLookupService.performLookup(lookupParams).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.handleLookupResult(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Failed',
            detail: response.message || 'Failed to perform lookup'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error performing lookup:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Lookup Error',
          detail: error.error?.message || 'Failed to perform lookup'
        });
      }
    });
  }

  // Address-based lookup (manual)
  async checkAddress(): Promise<void> {
    if (this.lookupMode !== 'MANUAL_ADDRESS') {
      return;
    }

    if (!this.selectedCountry || !this.city || !this.streetAddress || !this.selectedProducts || this.selectedProducts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Incomplete Information',
        detail: 'Please fill in all required fields and select at least one product'
      });
      return;
    }

    const addressParts = [
      this.streetAddress.trim(),
      this.addressLine2 ? this.addressLine2.trim() : '',
      this.city.trim(),
      this.stateRegion ? this.stateRegion.trim() : '',
      this.postalCode ? this.postalCode.trim() : '',
      this.selectedCountry.label
    ].filter(part => !!part);

    const fullAddress = addressParts.join(', ');
    this.loading = true;

    this.customerLookupService.geocodeAddress(fullAddress).subscribe({
      next: (geocodeResponse) => {
        this.loading = false;
        if (geocodeResponse && geocodeResponse.success) {
          const geocodeResult = geocodeResponse.data;
          this.manualGeocodedLocation = {
            lat: geocodeResult.lat,
            lng: geocodeResult.lng
          };
          this.manualAddressPlaceId = geocodeResult.placeId || null;
          this.manualPlaceName = geocodeResult.name || this.manualPlaceName;
          this.manualAddressReady = true;
          setTimeout(() => {
            if (this.manualGeocodedLocation) {
              this.renderManualAddressMap(this.manualGeocodedLocation);
            }
          }, 0);
          this.manualAddressMatchesPage = 1;
          this.loadManualAddressCandidates();
          this.evaluateManualAddressConflicts();
          this.messageService.add({
            severity: 'success',
            summary: 'Address Located',
            detail: 'Verify the marker location and run the manual lookup.'
          });
        } else {
          this.manualGeocodedLocation = null;
          this.manualAddressReady = false;
          this.manualAddressMatches = [];
          this.manualAddressMatchesTotal = 0;
          this.manualAddressSelectedCandidate = null;
          this.manualAddressPlaceId = null;
          this.messageService.add({
            severity: 'error',
            summary: 'Geocoding Failed',
            detail: 'Could not find coordinates for this address'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        this.manualGeocodedLocation = null;
        this.manualAddressReady = false;
        this.manualAddressMatches = [];
        this.manualAddressMatchesTotal = 0;
        this.manualAddressSelectedCandidate = null;
        this.manualAddressPlaceId = null;
        console.error('Error geocoding address:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Geocoding Error',
          detail: 'Failed to geocode the address'
        });
      }
    });
  }

  performManualAddressLookup(): void {
    if (this.lookupMode !== 'MANUAL_ADDRESS') {
      return;
    }

    // Clear past lookup results when starting new lookup
    this.lookupResults = [];
    // Reset product selection to all products
    this.resetProductSelection();

    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Product Selected',
        detail: 'Please select at least one product'
      });
      return;
    }

    if (!this.manualGeocodedLocation) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Location Not Ready',
        detail: 'Please locate the address on the map first'
      });
      return;
    }

    if (!this.selectedCountry || !this.city || !this.streetAddress) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Incomplete Information',
        detail: 'Please provide the required address details'
      });
      return;
    }

    const manualAddressPayload = {
      line1: this.streetAddress.trim(),
      line2: this.addressLine2 ? this.addressLine2.trim() : undefined,
      city: this.city.trim(),
      state: this.stateRegion ? this.stateRegion.trim() : undefined,
      postalCode: this.postalCode ? this.postalCode.trim() : undefined,
      countryCode: this.selectedCountry.value,
      label: this.manualPlaceName ? this.manualPlaceName.trim() : undefined
    };

    const coordinates = this.manualGeocodedLocation;
    const placeId = this.manualAddressPlaceId || this.manualAddressSelectedCandidate?.placeId;

    const lookupParams: LookupParams = {
      productIds: this.selectedProducts,
      coordinates: coordinates || undefined,
      lookupSource: placeId ? 'GOOGLE' : 'MANUAL_ADDRESS',
      placeId: placeId || undefined,
      manualAddress: manualAddressPayload,
      manualGeofence: this.buildDefaultGeofence(coordinates || undefined, this.manualPlaceName || manualAddressPayload.line1)
    };

    this.loading = true;
    this.customerLookupService.performLookup(lookupParams).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.handleLookupResult(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Failed',
            detail: response.message || 'Failed to perform manual address lookup'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error performing manual address lookup:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Lookup Error',
          detail: error.error?.message || 'Failed to perform manual address lookup'
        });
      }
    });
  }

  // Select business from address search results
  selectAddressBusiness(business: any): void {
    this.showAddressBusinessResults = false;
    
    // Clear past lookup results when starting new lookup
    this.lookupResults = [];
    // Reset product selection to all products
    this.resetProductSelection();
    
    // Perform lookup for the selected business
    const lookupParams: LookupParams = {
      productIds: this.selectedProducts,
      placeId: business.place_id
    };

    this.loading = true;
    this.customerLookupService.performLookup(lookupParams).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.handleLookupResult(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Failed',
            detail: response.message || 'Failed to perform lookup for selected business'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error performing lookup:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Lookup Error',
          detail: error.error?.message || 'Failed to perform lookup'
        });
      }
    });
  }

  // Handle lookup result from API
  private handleLookupResult(result: LookupResult): void {
    const existingIndex = this.lookupResults.findIndex(existing => this.isSameLookup(existing, result));
    if (existingIndex >= 0) {
      const mergedResult = this.mergeLookupResults(this.lookupResults[existingIndex], result);
      this.lookupResults[existingIndex] = mergedResult;
      this.reorderLookupResult(existingIndex);
      this.messageService.add({
        severity: 'info',
        summary: 'Existing Customer Matched',
        detail: 'Matched an existing record using location and address checks.'
      });
    } else {
      this.lookupResults.unshift(result);
      this.messageService.add({
        severity: result.overallStatus === 'PASS' ? 'success' : 'info',
        summary: 'Lookup Complete',
        detail: result.overallMessage
      });
    }
  }

  // Get product names for display
  getSelectedProductNames(): string[] {
    return this.selectedProducts.map(productId => {
      const product = this.productOptions.find(p => p.value === productId);
      return product ? product.label : productId;
    });
  }

  // Get validation status CSS class
  getValidationStatusClass(status: string): string {
    switch (status) {
      case 'CLEAR_TO_APPROACH':
        return 'status-clear';
      case 'NOT_AVAILABLE_IN_TERRITORY':
        return 'status-not-available';
      case 'ALREADY_RESERVED':
        return 'status-reserved';
      case 'ALREADY_IN_CONTRACT':
        return 'status-contract';
      case 'NO_FSR_ACCESS':
        return 'status-no-access';
      case 'TERRITORY_NOT_FOUND':
        return 'status-no-territory';
      default:
        return 'status-unknown';
    }
  }

  // Get validation status display text
  getValidationStatusText(status: string): string {
    switch (status) {
      case 'CLEAR_TO_APPROACH':
        return 'Clear to Approach';
      case 'NOT_AVAILABLE_IN_TERRITORY':
        return 'Not Available in Territory';
      case 'ALREADY_RESERVED':
        return 'Already Reserved';
      case 'ALREADY_IN_CONTRACT':
        return 'Already in Contract';
      case 'NO_FSR_ACCESS':
        return 'No FSR Access';
      case 'TERRITORY_NOT_FOUND':
        return 'Territory Not Found';
      default:
        return 'Unknown Status';
    }
  }

  // Check if any product validation failed
  hasFailedProducts(result: LookupResult): boolean {
    return result.productValidations.some(validation => 
      validation.status !== 'CLEAR_TO_APPROACH'
    );
  }

  // Get failed products from a result
  getFailedProducts(result: LookupResult): any[] {
    return result.productValidations.filter(validation => 
      validation.status !== 'CLEAR_TO_APPROACH'
    );
  }

  // Get tooltip message for disabled Create Reservation button
  getCreateReservationTooltip(result: LookupResult): string {
    if (!this.hasFailedProducts(result)) {
      return '';
    }
    
    const failedProducts = this.getFailedProducts(result);
    const failedProductNames = failedProducts.map(p => p.productName).join(', ');
    return `Cannot create reservation. Failed products: ${failedProductNames}`;
  }

  // Get overall customer approachable status
  getCustomerApproachableStatus(result: LookupResult): string {
    // First check territory access - this takes priority over everything else
    if (result.territoryInfo && !result.territoryInfo.fsrHasAccess) {
      return 'FAILED';
    }
    
    // Then check for failed products
    if (this.hasFailedProducts(result)) {
      return 'FAILED';
    }
    
    return 'PASS';
  }

  // Get overall status CSS class
  getOverallStatusClass(result: LookupResult): string {
    return this.getCustomerApproachableStatus(result) === 'PASS' ? 'status-clear' : 'status-failed';
  }

  // Get priority message based on failure hierarchy
  getPriorityMessage(result: LookupResult): string {
    // First priority: Territory access failure
    if (result.territoryInfo && !result.territoryInfo.fsrHasAccess) {
      return result.territoryInfo.accessMessage || 'FSR does not have access to this territory';
    }
    
    // Second priority: Failed products
    if (this.hasFailedProducts(result)) {
      const failedProducts = this.getFailedProducts(result);
      if (failedProducts.length === 1) {
        return `Product "${failedProducts[0].productName}" failed validation: ${failedProducts[0].statusMessage}`;
      } else {
        return `${failedProducts.length} product(s) failed validation. Remove failed products to recheck.`;
      }
    }
    
    // Default: Use the overall message from the backend
    return result.overallMessage;
  }

  // Remove failed products and recheck
  removeFailedProductsAndRecheck(result: LookupResult): void {
    const failedProducts = this.getFailedProducts(result);
    const failedProductIds = failedProducts.map(p => p.productId);
    
    // Remove failed products from selected products
    this.selectedProducts = this.selectedProducts.filter(productId => 
      !failedProductIds.includes(productId)
    );

    // Show message about removed products
    this.messageService.add({
      severity: 'info',
      summary: 'Products Removed',
      detail: `Removed ${failedProducts.length} failed product(s). Rechecking with remaining products...`
    });

    // Recheck with remaining products
    if (this.selectedProducts.length > 0) {
      this.recheckWithRemainingProducts(result);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Products Remaining',
        detail: 'All products failed validation. Please select different products.'
      });
    }
  }

  // Recheck with remaining products - supports all lookup types
  private recheckWithRemainingProducts(result: LookupResult): void {
    const lookupParams: LookupParams = {
      productIds: this.selectedProducts
    };

    // Handle different lookup types
    if (result.lookupSource === 'GOOGLE' && result.placeId && !this.isSyntheticPlaceId(result.placeId)) {
      // Google Place lookup - use placeId
      lookupParams.placeId = result.placeId;
      lookupParams.lookupSource = 'GOOGLE';
      if (result.coordinates) {
        lookupParams.coordinates = result.coordinates;
        lookupParams.manualGeofence = this.buildDefaultGeofence(result.coordinates, result.businessName);
      }
    } else if (result.lookupSource === 'MANUAL_ADDRESS' && result.manualAddress && result.coordinates) {
      // Manual address lookup - reconstruct from result data
      lookupParams.coordinates = result.coordinates;
      lookupParams.lookupSource = 'MANUAL_ADDRESS';
      lookupParams.manualAddress = {
        line1: result.manualAddress.line1,
        line2: result.manualAddress.line2,
        city: result.manualAddress.city,
        state: result.manualAddress.state,
        postalCode: result.manualAddress.postalCode,
        countryCode: result.manualAddress.countryCode,
        label: result.manualAddress.label
      };
      lookupParams.placeId = result.placeId && !this.isSyntheticPlaceId(result.placeId) ? result.placeId : undefined;
      lookupParams.manualGeofence = this.buildDefaultGeofence(result.coordinates, result.manualAddress.label || result.businessName);
    } else if (result.lookupSource === 'MANUAL_GEOFENCE' && result.manualGeofence && result.coordinates) {
      // Manual geofence lookup - reconstruct from result data
      lookupParams.coordinates = result.coordinates;
      lookupParams.lookupSource = 'MANUAL_GEOFENCE';
      lookupParams.manualGeofence = {
        center: result.manualGeofence.center || result.coordinates,
        radiusMeters: result.manualGeofence.radiusMeters || this.MIN_GEOFENCE_RADIUS,
        label: result.manualGeofence.label
      };
    } else if (result.placeId && !this.isSyntheticPlaceId(result.placeId)) {
      // Fallback: try with placeId if available (for Google lookups)
      lookupParams.placeId = result.placeId;
      lookupParams.lookupSource = 'GOOGLE';
      if (result.coordinates) {
        lookupParams.coordinates = result.coordinates;
        lookupParams.manualGeofence = this.buildDefaultGeofence(result.coordinates, result.businessName);
      }
    } else if (result.coordinates) {
      // Last resort: use coordinates only
      lookupParams.coordinates = result.coordinates;
      lookupParams.manualGeofence = this.buildDefaultGeofence(result.coordinates, result.businessName);
      this.messageService.add({
        severity: 'warn',
        summary: 'Limited Recheck',
        detail: 'Rechecking with coordinates only. Some features may not be available.'
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Recheck Not Available',
        detail: 'This lookup cannot be rechecked automatically because it does not have sufficient reference data.'
      });
      return;
    }

    this.loading = true;
    this.customerLookupService.performLookup(lookupParams).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.success) {
          this.handleLookupResult(response.data);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Recheck Failed',
            detail: response.message || 'Failed to recheck with remaining products'
          });
        }
      },
      error: (error) => {
        this.loading = false;
        console.error('Error rechecking:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Recheck Error',
          detail: error.error?.message || 'Failed to recheck'
        });
      }
    });
  }

  // Load city suggestions when country changes
  onCountryChange(): void {
    if (this.selectedCountry) {
      this.citySuggestions = [];
      this.streetSuggestions = [];
      this.city = '';
      this.streetAddress = '';
      
      this.customerLookupService.getCitiesByCountry(this.selectedCountry.value).subscribe({
        next: (response) => {
          if (response.success) {
            this.citySuggestions = response.data;
          }
        },
        error: (error) => {
          console.error('Error loading cities:', error);
        }
      });
    }
  }

  // Load street suggestions when city changes
  onCityChange(): void {
    if (this.selectedCountry && this.city) {
      this.streetSuggestions = [];
      this.streetAddress = '';
      
      this.customerLookupService.getStreetsByCity(this.selectedCountry.value, this.city).subscribe({
        next: (response) => {
          if (response.success) {
            this.streetSuggestions = response.data;
          }
        },
        error: (error) => {
          console.error('Error loading streets:', error);
        }
      });
    }
  }

  // Select city from suggestions
  selectCity(city: string): void {
    this.preventClosure = true;
    this.city = city;
    this.showCitySuggestions = false;
    this.onCityChange();
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.preventClosure = false;
    }, 200);
  }

  // Select street from suggestions
  selectStreet(street: string): void {
    this.preventClosure = true;
    this.streetAddress = street;
    this.showStreetSuggestions = false;
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.preventClosure = false;
    }, 200);
  }

  // Map business search
  searchMapBusinesses(queryInput: any): void {
    const query = typeof queryInput === 'string' ? queryInput : queryInput?.target?.value;
    this.manualGeofenceSearchQuery = query ?? this.manualGeofenceSearchQuery;

    if (!this.manualGeofenceSearchQuery || this.manualGeofenceSearchQuery.trim().length < 3) {
      this.mapBusinessSuggestions = [];
      return;
    }

    this.manualGeofenceSearchLoading = true;
    this.customerLookupService.searchBusinesses(this.manualGeofenceSearchQuery).subscribe({
      next: (response) => {
        this.manualGeofenceSearchLoading = false;
        if (response.success) {
          this.mapBusinessSuggestions = response.data;
        } else {
          this.mapBusinessSuggestions = [];
        }
      },
      error: (error) => {
        this.manualGeofenceSearchLoading = false;
        console.error('Error searching map businesses:', error);
        this.mapBusinessSuggestions = [];
      }
    });
  }

  // Select business from map search
  selectMapBusiness(business: any): void {
    this.manualGeofenceSearchQuery = business.name || business.description || '';
    this.mapBusinessSuggestions = [];
    
    // Center map on selected business
    if (business.geometry?.location) {
      const position = {
        lat: business.geometry.location.lat,
        lng: business.geometry.location.lng
      };
      this.prepareGeofenceMap();
      if (this.manualGeofenceMap) {
        this.manualGeofenceMap.setCenter(position);
        this.manualGeofenceMap.setZoom(16);
      }
      this.setGeofenceCenter(position);
    } else if (business.place_id) {
      this.fetchGooglePlaceDetails(business.place_id).then(details => {
        if (details.coordinates) {
          this.prepareGeofenceMap();
          if (this.manualGeofenceMap) {
            this.manualGeofenceMap.setCenter(details.coordinates);
            this.manualGeofenceMap.setZoom(16);
          }
          this.setGeofenceCenter(details.coordinates);
        }
      }).catch(() => undefined);
    }
  }

  centerOnCurrentLocation(): void {
    if (!navigator.geolocation) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Geolocation Unavailable',
        detail: 'Your browser does not support geolocation.'
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.ngZone.run(() => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.prepareGeofenceMap();
          if (this.manualGeofenceMap) {
            this.manualGeofenceMap.setCenter(coords);
            this.manualGeofenceMap.setZoom(17);
          }
          this.setGeofenceCenter(coords);
        });
      },
      (error) => {
        this.ngZone.run(() => {
          console.error('Geolocation error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Location Error',
            detail: 'Unable to retrieve your current location.'
          });
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  // Initialize map
  initializeMap(): void {
    if (typeof google !== 'undefined' && google.maps) {
      const mapElement = document.getElementById('map');
      if (mapElement) {
        this.map = new google.maps.Map(mapElement, {
          center: { lat: 40.7128, lng: -74.0060 }, // Default to NYC
          zoom: 12,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true
        });
      }
    }
  }

  // Add markers to map
  addMapMarkers(businesses: any[]): void {
    // Clear existing markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    businesses.forEach(business => {
      if (business.geometry?.location) {
        const marker = new google.maps.Marker({
          position: {
            lat: business.geometry.location.lat,
            lng: business.geometry.location.lng
          },
          map: this.map,
          title: business.name,
          animation: google.maps.Animation.DROP
        });

        // Add info window
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div class="map-info-window">
              <h4>${business.name}</h4>
              <p>${business.formatted_address || 'Address not available'}</p>
              <button onclick="selectBusinessFromMap('${business.place_id}')" class="select-business-btn">
                Select This Business
              </button>
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(this.map, marker);
        });

        this.markers.push(marker);
      }
    });
  }

  // Center map on specific location
  centerMapOnLocation(position: { lat: number; lng: number }): void {
    if (this.map) {
      this.map.setCenter(position);
      this.map.setZoom(15);
    }
  }

  private fetchGooglePlaceDetails(placeId: string): Promise<{
    coordinates?: { lat: number; lng: number };
    name?: string;
    formattedAddress?: string;
  }> {
    if (!placeId) {
      return Promise.resolve({});
    }

    const cached = this.googlePlaceDetailsCache.get(placeId);
    if (cached) {
      this.selectedGooglePlaceDetails = {
        placeId,
        ...cached
      };
      return Promise.resolve({ ...cached });
    }

    return new Promise((resolve, reject) => {
      this.customerLookupService.getPlaceDetails(placeId).subscribe({
        next: (response) => {
          const payload = response?.data ?? response;
          const coordinates = payload?.geometry?.location ?? payload?.coordinates ?? null;
          const normalized = {
            coordinates: coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : undefined,
            name: payload?.name,
            formattedAddress: payload?.formatted_address || payload?.formattedAddress
          };

          if (normalized.coordinates) {
            const cacheValue = {
              coordinates: normalized.coordinates,
              name: normalized.name,
              formattedAddress: normalized.formattedAddress
            };
            this.googlePlaceDetailsCache.set(placeId, cacheValue);
          }
          this.selectedGooglePlaceDetails = {
            placeId,
            ...normalized
          };

          resolve(normalized);
        },
        error: (error) => {
          console.error('Error fetching Google place details:', error);
          reject(error);
        }
      });
    });
  }

  private buildDefaultGeofence(center?: { lat: number; lng: number }, label?: string) {
    if (!center) {
      return undefined;
    }

    return {
      center,
      radiusMeters: this.MIN_GEOFENCE_RADIUS,
      label: label ? label.trim() : undefined
    };
  }

  // Handle business selection from autocomplete
  onBusinessSelect(business: BusinessSuggestion): void {
    this.preventClosure = true;
    // Clear suggestions and update search query
    this.businessSuggestions = [];
    this.searchQuery = business.description;
    
    // Clear past lookup results when starting new lookup
    this.lookupResults = [];
    // Reset product selection to all products
    this.resetProductSelection();
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.preventClosure = false;
    }, 200);
    
    if (!this.selectedProducts || this.selectedProducts.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Product Selected',
        detail: 'Please select at least one product first'
      });
      return;
    }

    this.loading = true;

    this.fetchGooglePlaceDetails(business.place_id).then((details) => {
      const lookupParams: LookupParams = {
        productIds: this.selectedProducts,
        placeId: business.place_id,
        lookupSource: 'GOOGLE'
      };

      if (details?.coordinates) {
        lookupParams.coordinates = details.coordinates;
        lookupParams.manualGeofence = this.buildDefaultGeofence(details.coordinates, details?.name || business.description);
      }

      this.customerLookupService.performLookup(lookupParams).subscribe({
        next: (response) => {
          this.loading = false;
          if (response.success) {
            this.handleLookupResult(response.data);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Lookup Failed',
              detail: response.message || 'Failed to perform lookup'
            });
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error performing lookup:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Error',
            detail: error.error?.message || 'Failed to perform lookup'
          });
        }
      });
    }).catch(() => {
      const lookupParams: LookupParams = {
        productIds: this.selectedProducts,
        placeId: business.place_id,
        lookupSource: 'GOOGLE'
      };

      this.customerLookupService.performLookup(lookupParams).subscribe({
        next: (response) => {
          this.loading = false;
          if (response.success) {
            this.handleLookupResult(response.data);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Lookup Failed',
              detail: response.message || 'Failed to perform lookup'
            });
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error performing lookup:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Lookup Error',
            detail: error.error?.message || 'Failed to perform lookup'
          });
        }
      });
    });
  }

  // Get status color for UI
  getStatusSeverity(status: string): string {
    return status === 'PASS' ? 'success' : 'danger';
  }

  // Get status icon
  getStatusIcon(status: string): string {
    return status === 'PASS' ? 'pi pi-check-circle' : 'pi pi-times-circle';
  }

  private getLookupResultKey(result: LookupResult): string | null {
    if (!result) {
      return null;
    }

    if (result.customerId) {
      return `customer:${result.customerId}`;
    }

    if (result.placeId && !this.isSyntheticPlaceId(result.placeId)) {
      return `place:${result.placeId}`;
    }

    if (result.lookupSource === 'MANUAL_GEOFENCE' && result.manualGeofence) {
      const centerKey = this.getCoordinateKey(result.manualGeofence.center);
      if (centerKey) {
        const radius = typeof result.manualGeofence.radiusMeters === 'number'
          ? Math.round(result.manualGeofence.radiusMeters)
          : 0;
        return `geofence:${centerKey}:${radius}`;
      }
    }

    if (result.manualAddress) {
      const normalized = this.normalizeText(result.manualAddress.normalized);
      if (normalized) {
        return `manual-address:${normalized}`;
      }

      const fallback = [
        result.manualAddress.line1,
        result.manualAddress.line2,
        result.manualAddress.city,
        result.manualAddress.state,
        result.manualAddress.postalCode,
        result.manualAddress.countryCode
      ]
        .map(value => this.normalizeText(value))
        .filter(value => !!value)
        .join('|');

      if (fallback) {
        return `manual-address:${fallback}`;
      }
    }

    if (result.coordinates) {
      const coordKey = this.getCoordinateKey(result.coordinates);
      if (coordKey) {
        return `coords:${coordKey}`;
      }
    }

    if (result.formattedAddress) {
      const addressKey = this.normalizeText(result.formattedAddress);
      if (addressKey) {
        return `address:${addressKey}`;
      }
    }

    if (result.businessName) {
      const businessKey = this.normalizeText(result.businessName);
      if (businessKey) {
        return `business:${businessKey}`;
      }
    }

    return null;
  }

  private getCoordinateKey(coords?: { lat: number; lng: number }): string | null {
    if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') {
      return null;
    }

    const lat = coords.lat.toFixed(this.COORD_KEY_PRECISION);
    const lng = coords.lng.toFixed(this.COORD_KEY_PRECISION);
    return `${lat}:${lng}`;
  }

  private normalizeText(value?: string | null): string {
    return value ? value.trim().toLowerCase() : '';
  }

  private isSyntheticPlaceId(placeId?: string | null): boolean {
    if (!placeId) {
      return false;
    }
    return placeId.startsWith('coord_') || placeId.startsWith('manual_');
  }

  private mergeLookupResults(existing: LookupResult, incoming: LookupResult): LookupResult {
    const merged: LookupResult = { ...existing };

    // Prefer the most precise coordinates (incoming might have refined marker)
    merged.coordinates = incoming.coordinates ?? existing.coordinates;

    // Merge manual metadata
    merged.manualAddress = incoming.manualAddress ?? existing.manualAddress;
    merged.manualGeofence = incoming.manualGeofence ?? existing.manualGeofence;

    // Prefer non-synthetic place IDs, otherwise keep whichever is available
    const existingSynthetic = this.isSyntheticPlaceId(existing.placeId);
    const incomingSynthetic = this.isSyntheticPlaceId(incoming.placeId);
    if (!existingSynthetic) {
      merged.placeId = existing.placeId;
    } else if (!incomingSynthetic) {
      merged.placeId = incoming.placeId;
    } else {
      merged.placeId = incoming.placeId ?? existing.placeId;
    }

    // Merge customer ID if incoming has it
    merged.customerId = merged.customerId || incoming.customerId;

    // Combine lookup source to reflect richer context
    if (existing.lookupSource === incoming.lookupSource) {
      merged.lookupSource = existing.lookupSource;
    } else if (existing.lookupSource && incoming.lookupSource) {
      merged.lookupSource = incoming.lookupSource === 'GOOGLE' ? 'GOOGLE' : existing.lookupSource;
    } else {
      merged.lookupSource = incoming.lookupSource ?? existing.lookupSource;
    }

    // Preserve best overall status/message (prefer failing info to warn the user)
    if (incoming.overallStatus === 'FAIL' && merged.overallStatus !== 'FAIL') {
      merged.overallStatus = incoming.overallStatus;
      merged.overallMessage = incoming.overallMessage;
    } else if (!merged.overallMessage && incoming.overallMessage) {
      merged.overallMessage = incoming.overallMessage;
    }

    // Merge product validations - prefer the most recent details
    merged.productValidations = incoming.productValidations?.length
      ? incoming.productValidations
      : merged.productValidations;

    // Merge territory info if incoming has more details
    if (incoming.territoryInfo) {
      merged.territoryInfo = incoming.territoryInfo;
    }

    // Merge canRegister/view flags if incoming tightened access
    merged.canRegister = merged.canRegister && (incoming.canRegister ?? true);
    merged.canView = merged.canView || incoming.canView || false;

    return merged;
  }

  private reorderLookupResult(index: number): void {
    if (index <= 0 || index >= this.lookupResults.length) {
      return;
    }
    const [entry] = this.lookupResults.splice(index, 1);
    if (entry) {
      this.lookupResults.unshift(entry);
    }
  }

  private computeDistanceMeters(a?: { lat: number; lng: number }, b?: { lat: number; lng: number }): number {
    if (!a || !b || typeof a.lat !== 'number' || typeof a.lng !== 'number' || typeof b.lat !== 'number' || typeof b.lng !== 'number') {
      return Number.POSITIVE_INFINITY;
    }

    const R = 6371000; // metres
    const dLat = this.deg2rad(b.lat - a.lat);
    const dLng = this.deg2rad(b.lng - a.lng);
    const lat1 = this.deg2rad(a.lat);
    const lat2 = this.deg2rad(b.lat);

    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);

    const aa = sinDLat * sinDLat + sinDLng * sinDLng * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
    return R * c;
  }

  private deg2rad(value: number): number {
    return value * (Math.PI / 180);
  }

  private coordinatesClose(
    a?: { lat: number; lng: number },
    b?: { lat: number; lng: number },
    thresholdMeters: number = this.DUPLICATE_DISTANCE_THRESHOLD_METERS
  ): boolean {
    return this.computeDistanceMeters(a, b) <= thresholdMeters;
  }

  private getManualAddressSignature(result: LookupResult): string | null {
    if (!result.manualAddress) {
      return null;
    }

    const parts = [
      result.manualAddress.normalized,
      result.manualAddress.line1,
      result.manualAddress.line2,
      result.manualAddress.city,
      result.manualAddress.state,
      result.manualAddress.postalCode,
      result.manualAddress.countryCode
    ]
      .map(value => this.normalizeText(value))
      .filter(value => !!value);

    return parts.length > 0 ? parts.join('|') : null;
  }

  private isSameLookup(a: LookupResult, b: LookupResult): boolean {
    if (!a || !b) {
      return false;
    }

    if (a === b) {
      return true;
    }

    const signals = [
      this.hasMatchingCustomerId(a, b),
      this.hasMatchingPlaceId(a, b),
      this.coordinatesClose(a.coordinates, b.coordinates),
      this.manualAddressesMatch(a, b),
      this.geofencesOverlap(a, b),
      this.namesMatch(a, b),
      this.coordinatesWithinAnyGeofence(a, b)
    ].filter(Boolean).length;

    if (signals >= 2) {
      return true;
    }

    const keyA = this.getLookupResultKey(a);
    const keyB = this.getLookupResultKey(b);
    return !!keyA && !!keyB && keyA === keyB;
  }

  private hasMatchingCustomerId(a: LookupResult, b: LookupResult): boolean {
    return !!a.customerId && !!b.customerId && a.customerId === b.customerId;
  }

  private hasMatchingPlaceId(a: LookupResult, b: LookupResult): boolean {
    if (!a.placeId || !b.placeId) {
      return false;
    }

    if (a.placeId === b.placeId) {
      return true;
    }

    if (this.isSyntheticPlaceId(a.placeId) || this.isSyntheticPlaceId(b.placeId)) {
      return this.coordinatesClose(a.coordinates, b.coordinates);
    }

    return false;
  }

  private namesMatch(a: LookupResult, b: LookupResult): boolean {
    const nameA = this.normalizeText(a.businessName);
    const nameB = this.normalizeText(b.businessName);

    if (nameA && nameB && nameA === nameB) {
      return true;
    }

    const labelA = this.normalizeText(a.manualAddress?.label || a.manualGeofence?.label);
    const labelB = this.normalizeText(b.manualAddress?.label || b.manualGeofence?.label);

    return !!labelA && !!labelB && labelA === labelB;
  }

  private manualAddressesMatch(a: LookupResult, b: LookupResult): boolean {
    const addressSigA = this.getManualAddressSignature(a);
    const addressSigB = this.getManualAddressSignature(b);
    return !!addressSigA && !!addressSigB && addressSigA === addressSigB;
  }

  private geofencesOverlap(a: LookupResult, b: LookupResult): boolean {
    if (!a.manualGeofence || !b.manualGeofence) {
      return false;
    }

    const centerDistance = this.computeDistanceMeters(
      a.manualGeofence.center,
      b.manualGeofence.center
    );
    const radiusSum =
      Math.max(a.manualGeofence.radiusMeters ?? 0, this.MIN_GEOFENCE_RADIUS) +
      Math.max(b.manualGeofence.radiusMeters ?? 0, this.MIN_GEOFENCE_RADIUS);

    return centerDistance <= radiusSum + this.MIN_GEOFENCE_RADIUS;
  }

  private coordinatesWithinAnyGeofence(a: LookupResult, b: LookupResult): boolean {
    return (
      this.coordinateWithinGeofence(a.coordinates, b.manualGeofence) ||
      this.coordinateWithinGeofence(b.coordinates, a.manualGeofence)
    );
  }

  private coordinateWithinGeofence(
    coord?: { lat: number; lng: number },
    geofence?: { center: { lat: number; lng: number }; radiusMeters?: number }
  ): boolean {
    if (!coord || !geofence || !geofence.center) {
      return false;
    }

    const distance = this.computeDistanceMeters(coord, geofence.center);
    const radius = Math.max(geofence.radiusMeters ?? 0, this.MIN_GEOFENCE_RADIUS);
    return distance <= radius + this.MIN_GEOFENCE_RADIUS;
  }

  // Get result status class
  getResultStatusClass(status: string): string {
    return status === 'PASS' ? 'result-pass' : 'result-fail';
  }

  getLookupSourceLabel(result: LookupResult): string {
    switch (result.lookupSource) {
      case 'MANUAL_ADDRESS':
        return 'Manual Address';
      case 'MANUAL_GEOFENCE':
        return 'Manual Geofence';
      default:
        return 'Google Places';
    }
  }

  getLookupSourceIcon(result: LookupResult): string {
    switch (result.lookupSource) {
      case 'MANUAL_ADDRESS':
        return 'pi pi-map-marker';
      case 'MANUAL_GEOFENCE':
        return 'pi pi-circle';
      default:
        return 'pi pi-google';
    }
  }

  // Register new customer
  registerCustomer(result: LookupResult): void {
    this.selectedLookupResult = result;
    this.showAddCustomerDrawer = true;
    this.customerCreatedSuccess = false;
  }

  // View existing customer
  viewCustomer(result: LookupResult): void {
    this.messageService.add({
      severity: 'info',
      summary: 'View Customer',
      detail: `Redirecting to customer details for ${result.businessName}`
    });
    // TODO: Navigate to customer view
  }

  // Clear all results
  clearResults(): void {
    this.lookupResults = [];
    this.messageService.add({
      severity: 'info',
      summary: 'Results Cleared',
      detail: 'All lookup results have been cleared'
    });
  }

  // Reset search form
  resetSearch(): void {
    this.searchQuery = '';
    this.businessSuggestions = [];
    this.selectedCountry = null;
    this.city = '';
    this.streetAddress = '';
    this.addressLine2 = '';
    this.stateRegion = '';
    this.postalCode = '';
    this.selectedProducts = [];
    this.addressBusinessResults = [];
    this.showAddressBusinessResults = false;
    this.manualAddressReady = false;
    this.manualGeocodedLocation = null;
    this.manualGeofenceCenter = null;
    this.manualGeofenceLabel = '';
    this.manualGeofenceRadius = this.MIN_GEOFENCE_RADIUS;
    this.lookupMode = 'GOOGLE';
    this.resetManualAddressState();
    this.resetManualGeofenceState();
    this.messageService.add({
      severity: 'info',
      summary: 'Search Reset',
      detail: 'Search form has been reset'
    });
  }

  // Copy Place ID to clipboard
  copyToClipboard(placeId?: string): void {
    if (!placeId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Copy Not Available',
        detail: 'Nothing to copy for this result'
      });
      return;
    }

    if (navigator.clipboard && window.isSecureContext) {
      // Use modern clipboard API
      navigator.clipboard.writeText(placeId).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copied!',
          detail: 'Google Place ID copied to clipboard'
        });
      }).catch((error) => {
        console.error('Failed to copy to clipboard:', error);
        this.fallbackCopyToClipboard(placeId);
      });
    } else {
      // Fallback for older browsers or non-secure contexts
      this.fallbackCopyToClipboard(placeId);
    }
  }

  // Fallback copy method for older browsers
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      this.messageService.add({
        severity: 'success',
        summary: 'Copied!',
        detail: 'Google Place ID copied to clipboard'
      });
    } catch (error) {
      console.error('Fallback copy failed:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Copy Failed',
        detail: 'Failed to copy Place ID to clipboard'
      });
    } finally {
      document.body.removeChild(textArea);
    }
  }

  // Handle customer creation success
  onCustomerCreated(customerData: any): void {
    this.customerCreatedSuccess = true;
    this.showAddCustomerDrawer = false;
    
    // Update the lookup result to show it as registered
    if (this.selectedLookupResult) {
      this.selectedLookupResult.canRegister = false;
      this.selectedLookupResult.canView = true;
      this.selectedLookupResult.customerId = customerData.customerId;
    }

    // Store customer data and show post-creation dialog
    this.createdCustomerData = customerData;
    this.showPostCreationDialog = true;
  }
  
  // Handle Create Reservation button in post-creation dialog
  onPostCreationCreateReservation(): void {
    this.showPostCreationDialog = false;
    
    if (!this.createdCustomerData || !this.createdCustomerData.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID not found'
      });
      return;
    }

    // Navigate to add-reservation with pre-filled data
    this.router.navigate(['/admin/add-reservation'], {
      queryParams: {
        customerId: this.createdCustomerData.customerId,
        productIds: this.selectedProducts.join(','),
        businessName: this.createdCustomerData.businessName
      }
    });
    
    this.createdCustomerData = null;
  }
  
  // Handle Proceed without Reservation button
  onPostCreationProceedWithoutReservation(): void {
    this.showPostCreationDialog = false;
    
    // Verify customer was created
    if (!this.createdCustomerData || !this.createdCustomerData.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer creation failed. Please try again.'
      });
      return;
    }
    
    this.messageService.add({
      severity: 'success',
      summary: 'Customer Registered',
      detail: `${this.createdCustomerData?.businessName || 'Customer'} has been registered successfully!`
    });
    
    // Navigate to customers page
    setTimeout(() => {
      this.router.navigate(['/admin/customers']);
    }, 500);
    
    this.createdCustomerData = null;
  }

  // Handle drawer close
  onDrawerClose(): void {
    this.showAddCustomerDrawer = false;
    this.selectedLookupResult = null;
  }

  // Create reservation for customer
  createReservation(result: LookupResult): void {
    if (!result.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer ID not found. Please register the customer first.'
      });
      return;
    }

    // Check if any products failed validation
    if (this.hasFailedProducts(result)) {
      const failedProducts = this.getFailedProducts(result);
      const failedProductNames = failedProducts.map(p => p.productName).join(', ');
      
      // Offer to remove failed products and proceed
      this.confirmationService.confirm({
        message: `Some products (${failedProductNames}) are not available. Would you like to remove these products and create a reservation with the remaining available products?`,
        header: 'Remove Unavailable Products?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Remove and Create Reservation',
        rejectLabel: 'Cancel',
        accept: () => {
          // Remove failed products from selected products
          const failedProductIds = failedProducts.map(p => p.productId);
          const availableProducts = this.selectedProducts.filter(productId => 
            !failedProductIds.includes(productId)
          );
          
          if (availableProducts.length === 0) {
            this.messageService.add({
              severity: 'warn',
              summary: 'No Products Available',
              detail: 'All selected products failed validation. Please select different products.'
            });
            return;
          }
          
          // Update selected products
          this.selectedProducts = availableProducts;
          
          // Navigate to add-reservation with only available products
          this.router.navigate(['/admin/add-reservation'], {
            queryParams: {
              customerId: result.customerId,
              productIds: availableProducts.join(','),
              businessName: result.businessName
            }
          });
          
          this.messageService.add({
            severity: 'info',
            summary: 'Products Removed',
            detail: `Removed ${failedProducts.length} unavailable product(s). Creating reservation with remaining products.`
          });
        },
        reject: () => {
          // User cancelled - do nothing
        }
      });
      return;
    }

    // Navigate to add-reservation with pre-filled data
    this.router.navigate(['/admin/add-reservation'], {
      queryParams: {
        customerId: result.customerId,
        productIds: this.selectedProducts.join(','),
        businessName: result.businessName
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up event listener
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener);
    }
    // Clear any pending timeout
    if (this.radiusChangeTimeout) {
      clearTimeout(this.radiusChangeTimeout);
    }
    this.clearTerritoryOverlays();
  }
}
